import type { StructureFormat } from '@/formats/inputSourcesParsers/StructureFormat';
import {
  BLC,
  Block,
  Cell,
  type Label,
  type LabelFormat,
  Line,
  type Onset,
} from '@/data';
import type { BLCSettings } from '@/data/BlockLineCell';
import { MusicalTimeCoordinate } from '@/data/BlockLineCell';
import LabelParser from '@/formats/LabelParser';

export class StructureBLCParser {
  static generateBLCStructure(format: StructureFormat): Block[] {
    const byOnset = (a: Label, b: Label) => a.onset - b.onset;
    const labelsFromJSON = format.labels || [] as LabelFormat[];
    const labels: Label[] = labelsFromJSON
      .map((label) => LabelParser.fromFormat(label) as Label)
      .sort(byOnset);
    const settings: BLCSettings = this.parseBLCSettings(format);

    if (labels.length === 0) throw new Error('No labels found.');

    return this.generateBLCFromLabels(labels, settings);
  }

  private static parseBLCSettings(format: StructureFormat): BLCSettings {
    return {
      block: format.block || BLC.defaultBlockLabel,
      line: format.line || BLC.defaultLineLabel,
      cell: format.cell || BLC.defaultCellLabel,
    };
  }

  private static generateBLCFromLabels(labels: Label[], settings: BLCSettings): Block[] {
    const labelsBlock = labels.filter((l) => l.type === settings.block);
    const labelsLine = labels.filter((l) => l.type === settings.line);
    const labelsCells = labels.filter((l) => l.type === settings.cell);

    const BLCMusicalTime = this.calculateBLCMusicalTime(
      labelsBlock,
      labelsLine,
      labelsCells,
    );

    return this.generateBlocksFromLabels(labelsBlock, labelsLine, labelsCells, BLCMusicalTime);
  }

  private static calculateBLCMusicalTime(
    filteredLabelsBlock: Label[],
    filteredLabelsLine: Label[],
    filteredLabelsCell: Label[],
  ) {
    const maxOffset = [...filteredLabelsBlock, ...filteredLabelsLine, ...filteredLabelsCell]
      .map((label) => label.onset + label.duration)
      .reduce((previousOffset, offset) => (offset > previousOffset ? offset : previousOffset), 0);

    if (maxOffset === 0) throw new Error('Structure length is null');

    return new MusicalTimeCoordinate(0 as Onset, maxOffset as Onset);
  }

  private static generateBlocksFromLabels(
    labelsBlock: Label[],
    labelsLine: Label[],
    labelsCell: Label[],
    BLCMusicalTime: MusicalTimeCoordinate,
  ): Block[] {
    this.fillLabelsIfEmpty(labelsBlock, BLCMusicalTime);
    this.fillLabelIfNoLabelAtTheStart(labelsBlock, BLCMusicalTime);

    return labelsBlock.map((labelBlock: Label, index: number, blockLabels: Label[]) => {
      const offset = blockLabels[index + 1]
        ? blockLabels[index + 1].onset
        : BLCMusicalTime.offset;

      const blockCoordinate = new MusicalTimeCoordinate(labelBlock.onset, offset);

      const labelsLineForThisBlock = this.getLabelsFromTo(
        labelsLine,
        blockCoordinate.onset,
        blockCoordinate.offset,
      );

      const lines = this.generateLinesFromLabels(
        labelsLineForThisBlock,
        labelsCell,
        blockCoordinate,
      );

      return new Block(lines, blockCoordinate.onset, blockCoordinate.offset);
    });
  }

  private static fillLabelsIfEmpty(labels: Label[], musicalTime: MusicalTimeCoordinate) {
    if (labels.length === 0) {
      labels.push({
        onset: musicalTime.onset,
        duration: musicalTime.duration,
      } as Label);
    }
  }

  private static fillLabelIfNoLabelAtTheStart(
    labels: Label[],
    levelAbove: MusicalTimeCoordinate,
  ) {
    const firstLabel = labels[0];
    if (firstLabel.onset !== levelAbove.onset) {
      const fillTheGap = {
        onset: levelAbove.onset,
      } as Label;

      labels.unshift(fillTheGap);
    }
  }

  private static getLabelsFromTo(labels: Label[], fromOnset: Onset, toOnset: Onset) {
    return labels.filter((l) => (l.onset >= fromOnset) && (l.onset < toOnset));
  }

  private static generateLinesFromLabels(
    labelsLine: Label[],
    labelsCell: Label[],
    blockCoordinate: MusicalTimeCoordinate,
  ) {
    this.fillLabelsIfEmpty(labelsLine, blockCoordinate);
    this.fillLabelIfNoLabelAtTheStart(labelsLine, blockCoordinate);

    return labelsLine.map((labelLine, index, lineLabels) => {
      const lineOffset = lineLabels[index + 1]
        ? lineLabels[index + 1].onset
        : blockCoordinate.offset;

      const lineCoordinate = new MusicalTimeCoordinate(labelLine.onset, lineOffset);

      const labelsCellsForThisBlock = this.getLabelsFromTo(
        labelsCell,
        lineCoordinate.onset,
        lineCoordinate.offset,
      );

      const cells = this.createCellsFromLabels(labelsCellsForThisBlock, lineCoordinate);
      return new Line(cells, labelLine.onset, lineOffset);
    });
  }

  private static createCellsFromLabels(
    labelsCell: Label[],
    lineCoordinate: MusicalTimeCoordinate,
  ): Cell[] {
    this.fillLabelsIfEmpty(labelsCell, lineCoordinate);
    this.fillLabelIfNoLabelAtTheStart(labelsCell, lineCoordinate);

    return labelsCell.map((l: Label, index, arrayLabels) => {
      const nextOnset: Onset = arrayLabels[index + 1]?.onset || lineCoordinate.offset;
      return new Cell(l.onset, nextOnset);
    });
  }
}
